import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-hot-toast";
import { useSelector } from 'react-redux';
import ReCAPTCHA from "react-google-recaptcha";
import Select from 'react-select'; 
import { 
  User, 
  Mail, 
  Lock, 
  Users, 
  Zap, 
  Calendar,
  CheckCircle,
  Loader2,
  Eye,
  EyeOff,
  UserPlus,
  ArrowLeft,
  Shield,
  FileText,
  X,
  Star,
  MessageCircle,
  Globe,
  Heart,
  Tag, // Add for interests
  GraduationCap ,
  CheckCircle2,
  RefreshCw,
  Timer
} from 'lucide-react';

const RegisterPage = () => {
  const navigateTo = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '', 
    lastName: '', 
    email: '', 
    password: '', 
    confirmPassword: '',
    communityType: '', 
    interests: [], 
    graduatedFromClass: '',
    graduatingYear: '',
    batchStartYear: '',
    batchEndYear: '',
    accept: false, 
    captchaToken: null, 
    userType: 'member'
  });

    const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpData, setOtpData] = useState({ email: '', userId: '' });
  const [otpForm, setOtpForm] = useState({
    otp: '',
    loading: false,
    resendLoading: false,
    timer: 300, // 5 minutes
    canResend: false
  });
  
  const portalInfo = useSelector((state) => state.portalInfo.data);
  const portalType = portalInfo?.portalType || 'Community';

  // Interest options for Community portal
  const interestOptions = [
    { value: 'technology', label: '💻 Technology', color: '#3B82F6' },
    { value: 'sports', label: '⚽ Sports', color: '#10B981' },
    { value: 'music', label: '🎵 Music', color: '#8B5CF6' },
    { value: 'art', label: '🎨 Art', color: '#F59E0B' },
    { value: 'science', label: '🔬 Science', color: '#06B6D4' },
    { value: 'reading', label: '📚 Reading', color: '#EF4444' },
    { value: 'travel', label: '✈️ Travel', color: '#84CC16' },
    { value: 'photography', label: '📸 Photography', color: '#EC4899' },
    { value: 'cooking', label: '👨‍🍳 Cooking', color: '#F97316' },
    { value: 'fitness', label: '💪 Fitness', color: '#22C55E' },
    { value: 'gaming', label: '🎮 Gaming', color: '#6366F1' },
    { value: 'business', label: '💼 Business', color: '#374151' },
    { value: 'writing', label: '✍️ Writing', color: '#DC2626' },
    { value: 'volunteering', label: '🤝 Volunteering', color: '#059669' }
  ];


    // Timer effect for OTP modal
  React.useEffect(() => {
    let interval = null;
    if (showOtpModal && otpForm.timer > 0) {
      interval = setInterval(() => {
        setOtpForm(prev => ({
          ...prev,
          timer: prev.timer - 1,
          canResend: prev.timer <= 1
        }));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, otpForm.timer]);

  // Format timer display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };




  const handleReCaptcha = (token) => setFormData({ ...formData, captchaToken: token });
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleInterestsChange = (selectedOptions) => {
    setFormData({ 
      ...formData, 
      interests: selectedOptions ? selectedOptions.map(option => option.value) : [] 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // if (!formData.captchaToken) {
    //   toast.error("Please complete the CAPTCHA.");
    //   setLoading(false);
    //   return;
    // }
    
    if (!formData.accept) {
      toast.error("You must accept the terms and conditions to register.");
      setLoading(false);
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match.");
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      setLoading(false);
      return;
    }

    // Portal-specific validations
    if (portalType === 'Community' && formData.interests.length === 0) {
      toast.error("Please select at least one interest.");
      setLoading(false);
      return;
    }

    if (portalType === 'School' && (!formData.graduatedFromClass || !formData.graduatingYear)) {
      toast.error("Please fill in all school-related fields.");
      setLoading(false);
      return;
    }

    if (portalType === 'University' && (!formData.batchStartYear || !formData.batchEndYear)) {
      toast.error("Please fill in batch start and end years.");
      setLoading(false);
      return;
    }
    
    try {
      const submitData = {
        ...formData,
        fullName: `${formData.firstName} ${formData.lastName}`,
        // Convert interests array to comma-separated string for storage
        interests: portalType === 'Community' ? formData.interests.join(', ') : undefined,
        // Set graduatedFromClass for school portal
        graduatedFromClass: portalType === 'School' ? parseInt(formData.graduatedFromClass) : undefined,
        // Set graduatingYear for school portal  
        graduatingYear: portalType === 'School' ? formData.graduatingYear : undefined,
        // Create batch string for university portal
        batch: portalType === 'University' ? `${formData.batchStartYear}-${formData.batchEndYear}` : undefined
      };
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/alumni/register`, submitData);
      // toast.success("Welcome to the community! Please login to continue.");
      console.log("response", response.data);
      console.log("portalType", portalType);
      console.log("response.data.success && response.data.data.otpSent && portalType == 'Community'", response.data.success && response.data.data.otpSent && portalType == 'Community')

      
       if (response.data.success && response.data.data.otpSent && portalType == 'Community') {
        // Show OTP modal instead of navigating
        setOtpData({
          email: response.data.data.email,
          userId: response.data.data.userId
        });
        setShowOtpModal(true);
        toast.dismiss()
        toast.success("Registration successful! Please check your email for verification code.");
      } else {
        toast.dismiss()
        toast.success("Registration successful! Please login to continue.");
        navigateTo('/login');
      }

      // navigateTo('/login');
    } catch (error) {
      console.log(error.response )
      toast.dismiss()
      toast.error(error.response?.data?.error || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

    // Handle OTP verification
  const handleOtpVerification = async (e) => {
    e.preventDefault();
     setOtpError('');
    if (!otpForm.otp || otpForm.otp.length !== 4) {
      // toast.error("Please enter a valid 4-digit OTP");
      setOtpError("Please enter a valid 4-digit OTP");
      return;
    }

    setOtpForm(prev => ({ ...prev, loading: true }));

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/alumni/verify-email-otp`, {
        email: otpData.email,
        otp: otpForm.otp
      });

      if (response.data.success) {
        toast.success("Email verified successfully! You can now login.");
        setShowOtpModal(false);
        navigateTo('/login');
      }
    } catch (error) {
        const errorMsg = error.response?.data?.message || "Invalid OTP. Please try again.";
         setOtpError(errorMsg);
      // toast.error(error.response?.data?.message || "Invalid OTP. Please try again.");
      setOtpForm(prev => ({ ...prev, otp: '' }));
    } finally {
      setOtpForm(prev => ({ ...prev, loading: false }));
    }
  };



    // Handle OTP resend
  const handleResendOtp = async () => {
    setOtpForm(prev => ({ ...prev, resendLoading: true }));

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/alumni/resend-verification-otp`, {
        email: otpData.email
      });

      if (response.data.success) {
        toast.success("New verification code sent to your email");
        setOtpForm(prev => ({
          ...prev,
          timer: 300,
          canResend: false,
          otp: ''
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error sending verification code");
    } finally {
      setOtpForm(prev => ({ ...prev, resendLoading: false }));
    }
  };


  // Generate year options (last 50 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 50 }, (_, i) => currentYear - i);

  // Generate class options (1st to 12th)
  const classOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
      minHeight: '42px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(168, 85, 247, 0.2)' : 'none',
      borderColor: state.isFocused ? '#A855F7' : '#E5E7EB',
      '&:hover': {
        borderColor: '#A855F7'
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#F3F4F6',
      borderRadius: '6px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: '14px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      ':hover': {
        backgroundColor: '#EF4444',
        color: 'white',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9CA3AF',
      fontSize: '14px'
    })
  };

  // Render portal-specific fields
  const renderPortalSpecificFields = () => {
    switch (portalType) {
      case 'Community':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Interests *
              </div>
            </label>
            <Select
              isMulti
              options={interestOptions}
              value={interestOptions.filter(option => formData.interests.includes(option.value))}
              onChange={handleInterestsChange}
              placeholder="Select your interests..."
              styles={customSelectStyles}
              className="text-sm"
              classNamePrefix="select"
            />
            <p className="text-xs text-gray-500 mt-1">Select topics you're passionate about</p>
          </div>
        );

      case 'School':
        return (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Graduated From Class *
                  </div>
                </label>
                <select
                  name="graduatedFromClass"
                  value={formData.graduatedFromClass}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
                >
                  <option value="">Select Class</option>
                  {classOptions.map(cls => (
                    <option key={cls} value={cls}>
                      {cls}{cls === 1 ? 'st' : cls === 2 ? 'nd' : cls === 3 ? 'rd' : 'th'} Class
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Graduating Year *
                  </div>
                </label>
                <select
                  name="graduatingYear"
                  value={formData.graduatingYear}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
                >
                  <option value="">Select Year</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        );

      case 'University':
        return (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Batch Start Year *
                  </div>
                </label>
                <select
                  name="batchStartYear"
                  value={formData.batchStartYear}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
                >
                  <option value="">Start Year</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Batch End Year *
                  </div>
                </label>
                <select
                  name="batchEndYear"
                  value={formData.batchEndYear}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
                >
                  <option value="">End Year</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            {formData.batchStartYear && formData.batchEndYear && (
              <div className="text-xs text-gray-500 mt-1">
                Batch: {formData.batchStartYear} - {formData.batchEndYear}
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-purple-50/50 via-blue-50/30 to-indigo-50/50">
      {/* Your existing background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-24 w-80 h-80 bg-gradient-to-br from-purple-200/30 to-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-24 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-indigo-200/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/4 w-48 h-48 bg-gradient-to-br from-purple-100/20 to-pink-100/20 rounded-full blur-2xl"></div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden relative h-screen flex flex-col py-2 px-3 z-10">
        {/* Your existing mobile header */}
        <div className="flex items-center justify-between mb-4 pt-2">
          <button 
            onClick={() => navigateTo('/login')}
            className="p-2 rounded-full bg-white/30 backdrop-blur-sm dynamic-site-text-1 hover:bg-white/40 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="w-12 h-12 dynamic-site-bg rounded-2xl flex items-center justify-center mx-auto mb-1">
              <span className="text-white font-bold text-lg">
                {portalType === 'Community' ? 'C' : portalType === 'School' ? 'S' : 'U'}
              </span>
            </div>
            <h1 className="text-lg font-bold dynamic-site-bg bg-clip-text text-transparent">
              {portalType} Portal
            </h1>
          </div>
          <div className="w-9"></div>
        </div>

        {/* Mobile Form */}
        <div className="flex-1 bg-white/70 backdrop-blur-2xl rounded-3xl shadow-xl p-2 lg:p-5 border border-white/30 overflow-y-auto">
         

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Existing name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  name="firstName"
                  onChange={handleChange}
                  required
                  placeholder="First Name"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  name="lastName"
                  onChange={handleChange}
                  required
                  placeholder="Last Name"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
                />
              </div>
            </div>

            {/* Email field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                name="email"
                type="email"
                onChange={handleChange}
                required
                placeholder="Enter your email"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
              />
            </div>

            {/* Password fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <input
                    name="password"
                    type={passwordVisible ? 'text' : 'password'}
                    onChange={handleChange}
                    required
                    placeholder="Password"
                    className="w-full px-3 py-2.5 pr-9 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2"
                  >
                    {passwordVisible ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm</label>
                <div className="relative">
                  <input
                    name="confirmPassword"
                    type={confirmPasswordVisible ? 'text' : 'password'}
                    onChange={handleChange}
                    required
                    placeholder="Confirm"
                    className="w-full px-3 py-2.5 pr-9 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm bg-white/80"
                  />
                  <button
                    type="button"
                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2"
                  >
                    {confirmPasswordVisible ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Portal-specific fields */}
            {renderPortalSpecificFields()}

            {/* Your existing reCAPTCHA, terms, and submit button */}
            {/* <div className="flex justify-center py-2">
              <ReCAPTCHA 
                sitekey="6LdPzXgqAAAAACrakqqSjHvl4XIVyec6u1UimfSM" 
                onChange={handleReCaptcha}
              />
            </div> */}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="accept"
                checked={formData.accept}
                onChange={handleChange}
                className="mt-1 w-4 h-4 dynamic-site-text-1 border-gray-300 rounded focus:ring-purple-400/20"
                required
              />
              <label className="text-xs text-gray-600">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowPrivacyPolicy(true)}
                  className="dynamic-site-text-1 hover:underline font-medium"
                >
                  Terms & Privacy Policy
                </button>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full dynamic-site-bg text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Join {portalType}
                </>
              )}
            </button>

            <div className="text-center text-sm pt-2">
              <span className="text-gray-500">Already have an account? </span>
              <button
                type="button"
                onClick={() => navigateTo('/login')}
                className="dynamic-site-text-1 font-semibold hover:underline"
              >
                Sign In
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex relative h-screen items-center justify-center p-4 z-10">
        <div className="w-full max-w-5xl bg-white/60 backdrop-blur-2xl rounded-3xl shadow-xl overflow-hidden border border-white/40 h-[85vh] max-h-[600px]">
          <div className="flex h-full">
            {/* Left Panel - Update based on portal type */}
            <div className="w-2/5 dynamic-site-bg text-white relative overflow-hidden flex flex-col rounded-l-3xl">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-8 left-8 w-24 h-24 bg-white rounded-full blur-2xl"></div>
                <div className="absolute bottom-16 right-8 w-16 h-16 bg-purple-200 rounded-full blur-xl"></div>
                <div className="absolute top-1/2 right-16 w-12 h-12 bg-white rounded-full blur-lg"></div>
              </div>
              
              {/* Logo Section */}
              <div className="relative z-10 flex items-center justify-center pt-6 pb-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-2 border border-white/30">
                    <span className="text-white font-bold text-xl">
                      {portalType === 'Community' ? 'C' : portalType === 'School' ? 'S' : 'U'}
                    </span>
                  </div>
                  <h1 className="text-2xl font-bold mb-1">{portalType} Portal</h1>
                  <p className="text-white/80 text-sm">Build Your {portalType}</p>
                </div>
              </div>

              {/* Features Content - customize based on portal type */}
              <div className="relative z-10 flex-1 flex flex-col justify-center px-8 space-y-4">
                {/* Your existing features content */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 transform hover:translate-x-1 transition-transform duration-300">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">CONNECT</h2>
                      <p className="text-white/70 text-xs">Build lasting relationships</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 transform hover:translate-x-1 transition-transform duration-300">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                      <MessageCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">COLLABORATE</h2>
                      <p className="text-white/70 text-xs">Work together on projects</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 transform hover:translate-x-1 transition-transform duration-300">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">GROW</h2>
                      <p className="text-white/70 text-xs">Expand your opportunities</p>
                    </div>
                  </div>
                </div>
                
                {/* Compact Community Stats */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                  <h3 className="text-sm font-semibold mb-3 text-center">Join Our Growing Community</h3>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="text-lg font-bold">50K+</div>
                      <div className="text-xs text-white/70">Members</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">1000+</div>
                      <div className="text-xs text-white/70">Communities</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">98%</div>
                      <div className="text-xs text-white/70">Satisfaction</div>
                    </div>
                  </div>
                </div>

                {/* Compact Trust Badges */}
                <div className="flex flex-wrap justify-center gap-3 text-xs">
                  <div className="flex items-center gap-1 text-white/60">
                    <Shield className="w-3 h-3 text-green-300" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/60">
                    <Globe className="w-3 h-3 text-blue-300" />
                    <span>Global</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/60">
                    <Heart className="w-3 h-3 text-pink-300" />
                    <span>Community First</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Registration Form */}
            <div className="w-3/5 p-6 overflow-y-auto rounded-r-3xl custom-scrollbar">
              <div className="max-w-md mx-auto">
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold text-gray-800 mb-1">Join Our {portalType}</h1>
                  <p className="text-gray-500 text-sm">Create your account to get started</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          name="firstName"
                          onChange={handleChange}
                          required
                          placeholder="First Name"
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all bg-white/80 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          name="lastName"
                          onChange={handleChange}
                          required
                          placeholder="Last Name"
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all bg-white/80 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        name="email"
                        type="email"
                        onChange={handleChange}
                        required
                        placeholder="Enter your email address"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all bg-white/80 text-sm"
                      />
                    </div>
                  </div>

                  {/* Password Fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          name="password"
                          type={passwordVisible ? 'text' : 'password'}
                          onChange={handleChange}
                          required
                          placeholder="Create password"
                          className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all bg-white/80 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setPasswordVisible(!passwordVisible)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-500"
                        >
                          {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          name="confirmPassword"
                          type={confirmPasswordVisible ? 'text' : 'password'}
                          onChange={handleChange}
                          required
                          placeholder="Confirm password"
                          className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all bg-white/80 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-500"
                        >
                          {confirmPasswordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Portal-specific fields */}
                  {renderPortalSpecificFields()}

                  {/* reCAPTCHA */}
                  {/* <div className="flex justify-center py-2">
                    <ReCAPTCHA 
                      sitekey="6LdPzXgqAAAAACrakqqSjHvl4XIVyec6u1UimfSM" 
                      onChange={handleReCaptcha}
                    />
                  </div> */}

                  {/* Terms & Privacy */}
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      name="accept"
                      checked={formData.accept}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 text-purple-500 border-gray-300 rounded focus:ring-2 focus:ring-purple-400/20"
                      required
                    />
                    <label className="text-sm text-gray-600">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setShowPrivacyPolicy(true)}
                        className="dynamic-site-text-1 font-semibold hover:underline transition-colors"
                      >
                        Terms & Privacy Policy
                      </button>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full dynamic-site-bg text-white py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating your account...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Join Our {portalType}
                      </>
                    )}
                  </button>

                  {/* Login Link */}
                  <div className="text-center pt-2">
                    <p className="text-gray-500 text-sm">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => navigateTo('/login')}
                        className="dynamic-site-text-1 font-semibold hover:underline transition-colors"
                      >
                        Sign In
                      </button>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Privacy Policy & Terms</h2>
                </div>
                <button
                  onClick={() => setShowPrivacyPolicy(false)}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4 text-sm overflow-y-auto max-h-[60vh] custom-scrollbar">
              <p>
                Welcome to <strong>Communiticate</strong>, your platform for building meaningful connections and thriving communities. We collect personal information such as your name, email address, community interests, and interaction data to create your profile and provide personalized community experiences. This data helps us connect you with like-minded individuals, suggest relevant communities, and enhance your networking opportunities.
              </p>
              <p>
                We are committed to protecting your privacy and do not sell your personal data to third parties for marketing purposes. All data is securely stored using industry-standard encryption, and we implement robust security measures to prevent unauthorized access. Your information is used solely to improve your community experience and facilitate meaningful connections.
              </p>
              <p>
                If you are a resident of the <strong>European Economic Area (EEA)</strong>, your data is protected under the <strong>General Data Protection Regulation (GDPR)</strong>. You have the right to access, modify, or delete your personal data at any time. You can also request a copy of your stored data or withdraw consent for data processing by contacting our support team.
              </p>
              <p>
                To enhance user experience, <strong>Communiticate</strong> may integrate with third-party services for authentication, analytics, communication, and security. These services comply with strict data protection standards and are carefully selected to ensure your privacy is maintained. We will always disclose third-party integrations and provide opt-out options where possible.
              </p>
              <p>
                We may update this <strong>Privacy Policy</strong> periodically to reflect changes in our services, legal requirements, or community feedback. Users will be notified of significant changes through email or platform notifications. Your continued use of <strong>Communiticate</strong> after these updates indicates acceptance of the revised policy.
              </p>
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                <p className="font-semibold text-purple-800">Community Guidelines:</p>
                <p className="text-purple-700">By joining Communiticate, you agree to maintain respectful interactions, protect other members' privacy, and contribute positively to our community ecosystem. Harassment, spam, or malicious behavior will result in account suspension.</p>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-200"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #a855f7 transparent;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 2px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #a855f7, #3b82f6);
          border-radius: 2px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #9333ea, #2563eb);
        }
      `}</style>


          {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="dynamic-site-bg text-white p-6 rounded-t-2xl">
              <div className="text-center">
                <div className="w-16 h-16 dynamic-site-bg-3 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 border border-white/30">
                  <Mail className="w-8 h-8" />
                </div>
                <h2 className="text-xl font-semibold">Verify Your Email</h2>
                <p className="text-white/80 text-sm mt-1">Enter the code sent to {otpData.email}</p>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
                {otpError && (
    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-center">
      <p className="font-medium">{otpError}</p>
    </div>
  )}
              <form onSubmit={handleOtpVerification} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={otpForm.otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setOtpForm(prev => ({ ...prev, otp: value }));
                    }}
                    placeholder="Enter 4-digit code"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-center text-2xl font-bold tracking-widest"
                    maxLength={4}
                  />
                </div>

                {/* Timer */}
                <div className="text-center">
                  {otpForm.timer > 0 ? (
                    <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                      <Timer className="w-4 h-4" />
                      <span>Code expires in {formatTime(otpForm.timer)}</span>
                    </div>
                  ) : (
                    <p className="text-red-500 text-sm">Code expired</p>
                  )}
                </div>

                {/* Buttons */}
                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={otpForm.loading || !otpForm.otp || otpForm.otp.length !== 4}
                    className="w-full dynamic-site-bg text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {otpForm.loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Verify Email
                      </>
                    )}
                  </button>

                  {otpForm.canResend && (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={otpForm.resendLoading}
                      className="w-full border border-purple-200 dynamic-site-text-3 py-3 rounded-xl font-semibold hover:bg-purple-50 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {otpForm.resendLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Resend Code
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Help text */}
                <div className="text-center text-xs text-gray-500 mt-4">
                  <p>Didn't receive the code? Check your spam folder or</p>
                  <button
                    type="button"
                    onClick={() => setShowOtpModal(false)}
                    className="dynamic-site-text-3 hover:underline"
                  >
                    go back to registration 
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
